import React, { useState, useEffect, useRef, useCallback } from 'react';
import { navigate } from 'gatsby';
import publicIp from 'public-ip';

import FormDropdown from './components/Dropdown';
import InputField from '../input-field/InputField';
import {
  validateRequired,
  validateEmail,
  validatePhone,
  validateSurName,
  validateName,
} from '../input-field/validation-helper';
import withToastProvider from './toast/withToastProvider';
import useToast from './toast/useToast';
import Attachment from './Attachment';
import { IPageContext } from '../../interfaces/pagecontext.interface';
import { IFileItem } from '../file-input/FileInput';
import { Localization } from '../../enums/localization.enum';

type TPrivacyStatementText = {
  listElement: string;
};

interface FormProps {
  onSubmit?: (data: any) => void;
  contactFormHeadings: {
    BeInformed: string;
    PrivacyLink: string;
    Purpose: string;
    AgreeSend: string;
    AllowedTypes: string;
    CoverLetter: string;
    AttachCV: string;
    Email: string;
    FirstName: string;
    HighSize: string;
    LastName: string;
    LinkedIn: string;
    PhoneNumber: string;
    PrivacyStatement: string;
    RemoveSelected: string;
    TotalSize: string;
    NameValidation: string;
    EmailValidation: string;
    PhoneValidation: string;
    ProjectValidation: string;
    CheckboxValidation: string;
    FileSizeValidation: {
      Title: string;
      Description: string;
    };
    TooMuchFilesValidation: {
      Title: string;
      Description: string;
    };
    SameFileValidation: {
      Title: string;
      Description: string;
    };
    Subject: string;
    SubjectsList: [
      {
        ListElement: string;
      },
    ];
    PrivacyStatementText: TPrivacyStatementText[];
  };
  formWithDropdown?: boolean;
  mcode: string;
  vacancyName?: string;
  courseName?: string;
  pageContext: IPageContext;
}
interface FileListProps {
  file?: {
    lastModified?: number;
    lastModifiedDate?: string;
    name?: string;
    size?: number;
    type?: string;
    webkitRelativePath?: string;
  };
  isChecked?: boolean;
}

const getSize = (files: File[]) => {
  return files.reduce((prev, cur) => {
    return prev + cur.size;
  }, 0);
};

const maxTotalSizeBytes = 11000000;
const maxFilesCount = 20;

const ContactForm = ({
  contactFormHeadings,
  formWithDropdown,
  mcode,
  courseName = '',
  vacancyName = '',
  pageContext,
}: FormProps) => {
  const fileInput = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<IFileItem[]>([]);
  const [name, setName] = useState<string>('');
  const [surname, setSurName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [project, setProject] = useState<string>('');
  const [linkedin, setLinkedin] = useState<string>();
  const [isNameValid, setIsNameValid] = useState<boolean>(true);
  const [isSurNameValid, setIsSurNameValid] = useState<boolean>(true);
  const [isEmailValid, setIsEmailValid] = useState<boolean>(true);
  const [isPhoneValid, setIsPhoneValid] = useState<boolean>(true);
  const [isProjectValid, setIsProjectValid] = useState<boolean>(true);
  const [isFileSizeValid, setIsFileSizeValid] = useState<boolean>(true);
  const [isFilesMaxCountValid, setIsFilesMaxCountValid] = useState<boolean>(true);
  const [isConfirmationValid, setIsConfirmationValid] = useState<boolean>(true);
  const [isChecked, setIsChecked] = useState<boolean>(false);
  const [isFormSending, setIsFormSending] = useState<boolean>(false);
  const [subject, setSubject] = useState<string>(contactFormHeadings.SubjectsList[0].ListElement);
  const [subjectIndex, setSubjectIndex] = useState<number>(0);
  const [pageMcode, setPageMcode] = useState<string>(formWithDropdown ? mcode[subjectIndex] : mcode);
  const [userIp, setUserIp] = useState<string>('');
  const { currentLocale } = pageContext;
  const currentPrefix = currentLocale === Localization.Uzbekistan ? Localization.Uzbekistan : '';

  useEffect(() => {
    publicIp.v4().then((data) => setUserIp(data));
  }, []);

  const toast = useToast();
  const validateForm = useCallback(() => {
    const isNameInputValid = validateName(name);
    const isSurNameInputValid = validateSurName(surname);
    const isEmailInputValid = validateEmail(email);
    const isPhoneInputValid = validatePhone(phone);
    const isProjectInputValid = validateRequired(project);

    setIsNameValid(isNameInputValid);
    setIsSurNameValid(isSurNameInputValid);
    setIsEmailValid(isEmailInputValid);
    setIsPhoneValid(isPhoneInputValid);
    setIsProjectValid(isProjectInputValid);
    setIsConfirmationValid(isChecked);

    return (
      isNameInputValid &&
      isSurNameInputValid &&
      isEmailInputValid &&
      isPhoneInputValid &&
      isProjectInputValid &&
      isFileSizeValid &&
      isFilesMaxCountValid &&
      isChecked
    );
  }, [name, surname, email, phone, project, isChecked, isFileSizeValid, isFilesMaxCountValid]);

  const handleOnSuccessSendEmail = useCallback(() => {
    (window as any).dataLayer?.push({
      event: 'ga_event',
      eventCategory: 'Form',
      eventAction: 'Request Sent',
    });

    return navigate(`${!currentPrefix.length ? '' : `/${currentPrefix}`}/thanks`);
  }, [currentPrefix]);

  useEffect(() => {
    window.contactFormConfig = {
      recaptchaKey: process.env.GOOGLE_CAPTCHA_API_KEY,
      apiKey: process.env.CONTACT_FORM_API_KEY,
      apiUri: process.env.CONTACT_FORM_API_URL,
      onSuccess: () => {
        handleOnSuccessSendEmail();
      },
      getCustomData: () => {
        return {
          exactTime: new Date().getTime(),
        };
      },
      onSubmit: () => {
        setIsFormSending(true);
      },
      onError: (serverError: string) => {
        validateForm();

        toast.add({
          title: 'Server error',
          description: serverError || 'Delivery failed',
        });
        (window as any).dataLayer?.push({
          event: 'ga_event',
          eventCategory: 'Form',
          eventAction: serverError ? 'Attachment Error' : 'Request Error',
        });
        setIsFormSending(false);
      },
    };
  }, [handleOnSuccessSendEmail, toast, validateForm]);

  useEffect(() => {
    const updateInputFileList = (filesArray: FileListProps[]) => {
      const list = new DataTransfer();

      filesArray.forEach((item) => {
        if (item.file) {
          list.items.add(new File([item.file as BlobPart], item.file.name as string, { type: item.file.type }));
        }
      });

      if (fileInput && fileInput.current && fileInput.current.files) {
        fileInput.current.files = list.files;
      }
    };

    updateInputFileList(files);
    setIsFilesMaxCountValid(files.length <= maxFilesCount);
  }, [files]);

  const handleOnNameChange = (value: string) => {
    setName(value);
  };

  const handleOnSurNameChange = (value: string) => {
    setSurName(value);
  };

  const handleOnEmailChange = (value: string) => {
    setEmail(value);
  };

  const handleOnPhoneChange = (value: string) => {
    setPhone(value);
  };

  const handleOnProjectChange = (value: string) => {
    setProject(value);
  };

  const handleOnSubjectChange = (value: string) => {
    setSubject(value);
  };

  const selectedIndexChange = (value: number) => {
    setSubjectIndex(value);
    setPageMcode(formWithDropdown ? mcode[value] : mcode);
  };

  const handleOnLinkedinChange = (value: string) => {
    setLinkedin(value);
  };

  const handleChange = () => {
    if (fileInput && fileInput.current && fileInput.current.files) {
      const selectedFiles = Object.values(fileInput.current.files);
      const addedFilesSize = getSize(files.map((item) => item.file));
      const noZeroSizeFiles = selectedFiles.filter((file) => file.size > 0);
      const uniqNameFiles = noZeroSizeFiles.filter((file) => !files.find((item) => item.file.name === file.name));
      const newFilesSize = getSize(uniqNameFiles);

      if (selectedFiles.length !== noZeroSizeFiles.length) {
        toast.add({
          title: contactFormHeadings.FileSizeValidation.Title,
          description: contactFormHeadings.FileSizeValidation.Description,
        });
      }

      if (selectedFiles.length > maxFilesCount) {
        toast.add({
          title: contactFormHeadings.TooMuchFilesValidation.Title,
          description: contactFormHeadings.TooMuchFilesValidation.Description,
        });
      }

      if (noZeroSizeFiles.length !== uniqNameFiles.length) {
        toast.add({
          title: contactFormHeadings.SameFileValidation.Title,
          description: contactFormHeadings.SameFileValidation.Description,
        });
      }

      setIsFileSizeValid(addedFilesSize + newFilesSize < maxTotalSizeBytes);
      setIsFilesMaxCountValid(selectedFiles.length <= maxFilesCount);

      setFiles(files.concat(uniqNameFiles.map((file) => ({ isChecked: false, file }))));
    }
  };

  const handleRemoveFiles = () => {
    const filteredFiles = files.filter((file) => !file.isChecked);

    setIsFileSizeValid(getSize(filteredFiles.map((item) => item.file)) < maxTotalSizeBytes);
    setIsFilesMaxCountValid(files.length <= maxFilesCount);
    setFiles(filteredFiles);
  };

  const handleFileItemChange = (fileName: string) => {
    const selectedFile = files.find((fileItem) => fileItem.file.name === fileName);

    if (selectedFile) {
      selectedFile.isChecked = !selectedFile.isChecked;
    }

    setFiles(files);
  };

  const loadWidget = () => {
    const script = document.createElement('script');

    script.src = `${process.env.CONTACT_FORM_WIDGET_URL}`;
    script.type = 'text/javascript';
    document.getElementsByTagName('head')[0].appendChild(script);
  };

  const handleFormFocus = () => {
    window.contactForm || loadWidget();
  };

  const handleOnConfirmationChange = () => {
    setIsChecked(!isChecked);
  };

  const handleSubmit = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();

    const isFormValid = validateForm();

    if (isFormValid) {
      setIsFormSending(true);
      window.contactForm.submit();
    }

    return true;
  };

  return (
    <form id="contact-form" action="" method="" className="contact-form form" onFocus={handleFormFocus}>
      <input type="hidden" id="mcode" value={pageMcode} contact-form-role="input" disabled />
      <input type="hidden" id="ip" value={userIp} contact-form-role="input" disabled />
      {vacancyName.length > 0 && (
        <input type="hidden" id="vacancy" value={vacancyName} contact-form-role="input" disabled />
      )}
      {courseName.length > 0 && (
        <input type="hidden" id="course" value={courseName} contact-form-role="input" disabled />
      )}
      <div className="main-grid-halved">
        <div className={!isNameValid ? 'main-grid-halved-item error-shown' : 'main-grid-halved-item'}>
          <InputField
            id="name"
            type="text"
            placeHolder={contactFormHeadings.FirstName}
            validation={{
              isValid: isNameValid,
              validationMessage: contactFormHeadings.NameValidation,
            }}
            onChange={handleOnNameChange}
            isRequired
            disabled={isFormSending}
            contactFormRole="input"
          />
        </div>
        <div className={!isSurNameValid ? 'main-grid-halved-item error-shown' : 'main-grid-halved-item'}>
          <InputField
            id="surname"
            type="text"
            placeHolder={contactFormHeadings.LastName}
            validation={{
              isValid: isSurNameValid,
              validationMessage: contactFormHeadings.NameValidation,
            }}
            onChange={handleOnSurNameChange}
            isRequired
            disabled={isFormSending}
            contactFormRole="input"
          />
        </div>
        <div className={!isEmailValid ? 'main-grid-halved-item error-shown' : 'main-grid-halved-item'}>
          <InputField
            id="email"
            type="email"
            placeHolder={contactFormHeadings.Email}
            validation={{
              isValid: isEmailValid,
              validationMessage: contactFormHeadings.EmailValidation,
            }}
            onChange={handleOnEmailChange}
            isRequired
            disabled={isFormSending}
            contactFormRole="input"
          />
        </div>
        <div className={!isPhoneValid ? 'main-grid-halved-item error-shown' : 'main-grid-halved-item'}>
          <InputField
            id="phone"
            type="tel"
            placeHolder={contactFormHeadings.PhoneNumber}
            validation={{
              isValid: isPhoneValid,
              validationMessage: contactFormHeadings.PhoneValidation,
            }}
            onChange={handleOnPhoneChange}
            isRequired
            disabled={isFormSending}
            contactFormRole="input"
          />
        </div>
      </div>

      <div className="contact-form-bottom">
        {formWithDropdown ? (
          <FormDropdown
            id="subject"
            options={contactFormHeadings.SubjectsList}
            contactFormRole="input"
            disabled={isFormSending}
            onChange={handleOnSubjectChange}
            selectedIndex={selectedIndexChange}
          />
        ) : (
          <InputField
            id="linkedin"
            type="text"
            placeHolder={contactFormHeadings.LinkedIn}
            onChange={handleOnLinkedinChange}
            disabled={isFormSending}
            contactFormRole="input"
            containerClassname="contact-form-linkedin"
          />
        )}

        <InputField
          id="message"
          type="textarea"
          placeHolder={contactFormHeadings.CoverLetter}
          validation={{
            isValid: isProjectValid,
            validationMessage: contactFormHeadings.ProjectValidation,
          }}
          onChange={handleOnProjectChange}
          isRequired
          disabled={isFormSending}
          contactFormRole="input"
        />
      </div>

      <Attachment
        inputRef={fileInput}
        files={files}
        onChange={handleChange}
        onChangeParagraph={handleOnConfirmationChange}
        checked={isChecked}
        onItemChange={handleFileItemChange}
        onRemove={handleRemoveFiles}
        onClick={handleSubmit}
        isFileSizeValid={isFileSizeValid}
        isFilesMaxCountValid={isFilesMaxCountValid}
        isRequired
        validation={{
          isValid: isConfirmationValid,
          validationMessage: contactFormHeadings.CheckboxValidation,
        }}
        disabled={isFormSending}
        sending={isFormSending}
        attachmentHeadings={contactFormHeadings}
        pageContext={pageContext}
      />
    </form>
  );
};

export default withToastProvider(ContactForm);
